import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Stars } from "../components/Stars";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LocationSelect } from "../components/LocationSelect";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { MapPin, Briefcase, Users } from "lucide-react";

export default function ProDirectory() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ sectors: [], locations: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: "all", location: "all" });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.sector !== "all") params.sector = filters.sector;
      if (filters.location !== "all") params.location = filters.location;
      const r = await api.get("/profiles", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const sectorLabel = (id) => {
    const s = meta.sectors.find((x) => x.id === id);
    return s ? (lang === "fr" ? s.fr : s.en) : id;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("pros_title")}</h1>
      <p className="text-muted-foreground mt-1 mb-8">{t("pros_sub")}</p>

      <div className="flex flex-wrap items-center gap-3 mb-8 p-4 rounded-2xl border border-border bg-card">
        <Select value={filters.sector} onValueChange={(v) => setFilters({ ...filters, sector: v })}>
          <SelectTrigger className="w-[200px]" data-testid="pro-sector-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("filter_sector")}</SelectItem>
            {meta.sectors.map((s) => <SelectItem key={s.id} value={s.id}>{lang === "fr" ? s.fr : s.en}</SelectItem>)}
          </SelectContent>
        </Select>
        <LocationSelect groups={meta.location_groups || []} value={filters.location}
          onChange={(v) => setFilters({ ...filters, location: v })} allLabel={t("filter_location")}
          testId="pro-location-filter" className="w-[200px]" />
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="pros-empty">
          <Users className="h-10 w-10 mx-auto mb-4 opacity-50" />
          {lang === "fr" ? "Aucun professionnel pour ces critères." : "No professionals for these criteria."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pros-grid">
          {items.map((p) => (
            <button
              key={p.user_id}
              data-testid={`pro-card-${p.user_id}`}
              onClick={() => navigate(`/pros/${p.user_id}`)}
              className="text-left rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center gap-4 mb-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={mediaUrl(p.photo_url)} alt={p.display_name} />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {p.display_name?.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h3 className="font-display font-semibold truncate">{p.display_name}</h3>
                  <div className="flex items-center gap-1.5">
                    <Stars value={p.avg_rating} size={14} />
                    <span className="text-xs text-muted-foreground">{p.avg_rating > 0 ? p.avg_rating : "—"} ({p.review_count})</span>
                  </div>
                </div>
              </div>
              {p.city && <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5 mb-3"><MapPin className="h-3.5 w-3.5" />{p.city}{p.years_experience ? ` · ${p.years_experience} ${t("years_exp")}` : ""}</p>}
              <div className="flex flex-wrap gap-1.5">
                {p.trade.slice(0, 3).map((tr) => (
                  <Badge key={tr} variant="secondary" className="rounded-full text-xs gap-1"><Briefcase className="h-3 w-3" />{sectorLabel(tr)}</Badge>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
